import React, { useState } from 'react'
// The lazy wrapper loads the Google Maps SDK when this screen mounts, instead
// of every page of the app paying for it (DT-012). Same props and children.
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete'
import PlacesAutocomplete from 'components/PlacesAutocompleteLazy'
import TWControls from 'components/controls/TWControls'
import { Form, useForm } from 'components/controls/useForm'
import { Search, ImageIcon } from 'lucide-react'
import Button from 'components/Button'
import Popup from 'components/Popup'
import ImagePickerPopup from 'components/popups/ImagePickerPopup'

const LocationAddNew = ({ locationAdd, popupGuide }) => {
	const [address, setAddress] = useState('')
	const [picLoaded, setPicLoaded] = useState(false)
	const [placeId, setPlaceId] = useState<string | null>(null)
	const [openImagePicker, setOpenImagePicker] = useState(false)

	const locationTypes = [
		{ selectValue: 'Commercial Center' },
		{ selectValue: 'Commercial Location' },
		{ selectValue: 'Retail Center' },
		{ selectValue: 'Retail Location' },
	]

	const initialFValues = {
		id: 0,
		user: '',
		assetName: '',
		assetType: 'location',
		locationType: 'Retail Location',
		retailer: '',
		address: '',
		city: '',
		stateProvince: '',
		postalCode: '',
		cbsa: '',
		country: '',
		latitude: '',
		longitude: '',
		sellingSqft: '',
		image: null,
		logo: null,
		googlePlaceId: null,
		updateNote: 'New Location',
	}

	const validate = () => {
		let temp: any = { ...errors }
		temp.assetName = values.assetName ? '' : '*Required field'
		temp.address = values.address ? '' : '*Required field'
		temp.city = values.city ? '' : '*Required field'
		temp.stateProvince = values.stateProvince ? '' : '*Required field'
		temp.postalCode = values.postalCode ? '' : '*Required field'

		setErrors({
			...temp,
		})

		return Object.values(temp).every(x => x === '')
	}

	const { values, setValues, errors, setErrors, handleInputChange, resetForm } = useForm(initialFValues, true, validate)

	const submitHandler = e => {
		e.preventDefault()
		if (validate() && picLoaded) {
			locationAdd(values, resetForm)
		}
	}

	const handleSetAddress = async value => {
		setAddress(value)
	}
	const handleSelect = async value => {
		const results = await geocodeByAddress(value)
		const latLng = await getLatLng(results[0])
		const place = results[0]
		setPlaceId(place.place_id)
		values.googlePlaceId = place.place_id

		setAddress(value)

		var newName = value.split(',')
		values.assetName = newName[0]
		values.address = ''
		values.postalCode = ''
		values.city = ''
		values.stateProvince = ''
		values.country = ''
		values.latitude = ''
		values.longitude = ''

		for (const component of place.address_components) {
			const componentType = component.types[0]

			switch (componentType) {
				case 'street_number': {
					values.address = `${component.long_name} ${values.address}`
					break
				}
				case 'route': {
					values.address += component.short_name
					break
				}
				case 'postal_code': {
					values.postalCode = `${component.long_name}${values.postalCode}`
					break
				}
				case 'postal_code_suffix': {
					values.postalCode = `${values.postalCode}-${component.long_name}`
					break
				}
				case 'locality':
					values.city = component.long_name
					break
				case 'administrative_area_level_1': {
					values.stateProvince = component.short_name
					break
				}
				case 'country':
					values.country = component.short_name
					break
				default:
					break
			}
		}
		values.latitude = latLng.lat
		values.longitude = latLng.lng
		setAddress('')
	}

	const closeForm = () => {
		setPicLoaded(true)
	}

	return (
		<Form onSubmit={submitHandler}>
			<div className="grid grid-cols-12 gap-2">
				{!values.image && (
					<div className="col-span-12">
						<p className="text-xs italic text-[#0080fe]">
							{`In the search box below type in a store name, address, city, or zip code. Once you've found your desired location, select it from the dropdown.`}
						</p>
					</div>
				)}

				<div className="col-span-8 self-end">
					<div className="relative">
						<PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleSelect}>
							{({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
								<div>
									<div className="relative">
										<span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
											<Search className="h-4 w-4" />
										</span>
										<TWControls.Input
											type="search"
											className="pl-9"
											{...getInputProps()}
										/>
									</div>

									<div className="absolute left-0 right-0 z-[9999]">
										<div className="bg-white rounded-lg shadow-sm">
											{loading ? <div className="p-2 text-sm"> ...loading</div> : null}

											{suggestions.map(suggestion => {
												const className = suggestion.active
													? 'px-3.5 py-1 bg-[#41b6e6] cursor-pointer text-sm'
													: 'px-3.5 py-1 bg-[#B9EDF8] cursor-pointer text-sm'

												return <div {...getSuggestionItemProps(suggestion, { className })}>{suggestion.description}</div>
											})}
										</div>
									</div>
								</div>
							)}
						</PlacesAutocomplete>
					</div>
				</div>
				<div className="col-span-4">
					<TWControls.SelectType
						size="small"
						name="locationType"
						label="Location Type"
						value={values.locationType}
						onChange={handleInputChange}
						options={locationTypes}
						className=""
					/>
				</div>

				<div className="col-span-6">
					<TWControls.Input
						size="small"
						name="assetName"
						label="Location Name"
						value={values.assetName}
						onChange={handleInputChange}
						// error={errors.assetName}
						className=""
					/>
				</div>
				<div className="col-span-6">
					<TWControls.Input
						size="small"
						name="address"
						label="Address"
						value={values.address}
						onChange={handleInputChange}
						// error={errors.address}
						className=""
					/>
				</div>
				<div className="col-span-4">
					<TWControls.Input
						size="small"
						name="city"
						label="City"
						value={values.city}
						onChange={handleInputChange}
						// error={errors.city}
						className=""
					/>
				</div>
				<div className="col-span-4">
					<TWControls.Input
						size="small"
						name="stateProvince"
						label="State/Prov"
						value={values.stateProvince}
						onChange={handleInputChange}
						// error={errors.stateProvince}
					/>
				</div>
				<div className="col-span-4">
					<TWControls.Input
						size="small"
						name="postalCode"
						label="Postal Code"
						value={values.postalCode}
						onChange={handleInputChange}
						// error={errors.postalCode}
					/>
				</div>
				<div className="col-span-4">
					<TWControls.Input
						size="small"
						name="latitude"
						label="Latitude"
						value={values.latitude}
						onChange={handleInputChange}
						// error={errors.latitude}
					/>
				</div>
				<div className="col-span-4">
					<TWControls.Input
						size="small"
						name="longitude"
						label="Longitude"
						value={values.longitude}
						onChange={handleInputChange}
						// error={errors.longitude}
					/>
				</div>
				<div className="col-span-4">
					<TWControls.Input
						size="small"
						name="country"
						label="Country"
						value={values.country}
						onChange={handleInputChange}
						// error={errors.country}
					/>
				</div>
				{values.image && (
					<div className="col-span-12">
						<div className="newLocation__imageHolder">
							<img className="newLocation__image" src={values.image} alt="" />
						</div>
					</div>
				)}

				{values.assetName && (
					<div className="col-span-12">
						{values.image ? (
							<div>
								<div className="newLocation__imageHolder">
									<Button variant="ghost" onClick={() => setOpenImagePicker(true)}>
										<ImageIcon className="w-4 h-4" /> Change Image
									</Button>
								</div>
								<div className="newLocation__imageHolder">
									<TWControls.Button type="submit" text="Add Location" onClick={closeForm} />
								</div>
							</div>
						) : (
							<div>
								<div className="newLocation__imageHolder">
									<Button variant="ghost" onClick={() => setOpenImagePicker(true)}>
										<ImageIcon className="w-4 h-4" /> Add Image
									</Button>
								</div>

								<div className="flex justify-center text-xs italic text-[#0080fe] mb-4">
									<p>{`Now select the 'Add Image' button above to import an image that represents the location you've selected.`}</p>
								</div>
								<div className="flex justify-center mt-4">
									<TWControls.Button text="Add Location" variant="ghost" />
								</div>
							</div>
						)}
					</div>
				)}
			</div>

			{openImagePicker && (
				<Popup
					title={`Select Image for "${values.assetName}"`}
					openPopup={openImagePicker}
					setOpenPopup={setOpenImagePicker}
					icon={<ImageIcon className="w-5 h-5" />}
					description="Select an image for this location"
				>
					<ImagePickerPopup
						placeId={placeId}
						locationName={values.assetName}
						onImageSelected={imageUrl => {
							values.image = imageUrl
							setPicLoaded(true)
							setOpenImagePicker(false)
						}}
					/>
				</Popup>
			)}
		</Form>
	)
}

export default LocationAddNew
